/**
 * # AsChangelog Component
 *
 * Fetches and displays a changelog, sorted by timestamp.
 *
 * ## Attributes
 * - `src`: Comma-separated URLs to `Changelog` JSON files. When multiple are provided,
 *   entries are merged by version (later sources override earlier ones).
 *
 * ## JSON Format
 * ```json
 * {
 *   "1.1.0": { "dateUnixMs": 1234567890000, "msg": "..." },
 *   "2.0.0": { "dateUnixMs": 1234567891000, "msg": { "prod": "...", "dev": "..." } }
 * }
 * ```
 * Entries are sorted by `dateUnixMs` (newest first).
 */
import styleText from './as-changelog.css?raw';
import { isProduction } from '../../data/constants.ts';
import { fetchResource } from '../../logic/asApi.ts';
import type { Changelog } from '../../data/data-interfaces.external.ts';
interface FlatEntry {
  version: string;
  dateUnixMs?: number;
  msg: string | { prod: string; dev: string };
}

export class AsChangelog extends HTMLElement {
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();

  static get observedAttributes(): string[] {
    return ['src'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsChangelog.sheet];
    this.shadowRoot!.innerHTML = '<div class="changelog-container"></div>';
  }

  private observer: IntersectionObserver | null = null;

  connectedCallback(): void {
    this.setupObserver();
  }

  disconnectedCallback(): void {
    this.observer?.disconnect();
    this.observer = null;
  }

  private setupObserver(): void {
    if (this.observer) this.observer.disconnect();
    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.load();
        this.observer?.disconnect();
        this.observer = null;
      }
    });
    this.observer.observe(this);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (name === 'src' && oldValue !== newValue && this.isConnected) {
      this._loadedSrc = null;
      this.setupObserver();
    }
  }

  private compareSemver(a: string, b: string): number {
    const pa = a.split('.').map(Number);
    const pb = b.split('.').map(Number);
    for (let i = 0; i < 3; i++) {
      const diff = (pb[i] || 0) - (pa[i] || 0);
      if (diff !== 0) return diff;
    }
    return 0;
  }

  private _loadedSrc: string | null = null;

  private async load(): Promise<void> {
    const src = this.getAttribute('src');
    if (this._loadedSrc === src) return;
    this._loadedSrc = src;

    const container = this.shadowRoot!.querySelector('.changelog-container')!;

    if (!src) {
      container.innerHTML = '<p class="changelog-empty">No changelog source specified.</p>';
      return;
    }

    try {
      const srcs = src.split(',').map(s => s.trim()).filter(Boolean);
      const merged: Changelog = {};
      for (const url of srcs) {
        const data = await fetchResource<Changelog>(url);
        if (data) Object.assign(merged, data);
      }

      if (Object.keys(merged).length === 0) {
        container.innerHTML = '<p class="changelog-empty">No changelog available.</p>';
        return;
      }

      const flat: FlatEntry[] = Object.entries(merged).map(([version, entry]) => ({
        version, dateUnixMs: entry.dateUnixMs, msg: entry.msg
      }));

      flat.sort((a, b) => {
        if (a.dateUnixMs != null && b.dateUnixMs != null) return b.dateUnixMs - a.dateUnixMs || this.compareSemver(a.version, b.version);
        if (a.dateUnixMs != null) return -1;
        if (b.dateUnixMs != null) return 1;
        return this.compareSemver(a.version, b.version);
      });

      container.innerHTML = flat.map(({ version, dateUnixMs, msg }) => {
        const dateStr = dateUnixMs != null
          ? new Date(dateUnixMs).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
          : null;
        const prodStr = typeof msg === 'string' ? msg : msg.prod;
        const devStr = typeof msg !== 'string' ? msg.dev : null;
        const msgBody = isProduction
          ? `<p class="changelog-msg">${prodStr}</p>`
          : /*html*/`
            <div class="changelog-msg-row">
              <span class="changelog-msg-label changelog-msg-label--prod">Prod:</span>
              <p class="changelog-msg">${prodStr}</p>
            </div>
            ${devStr ? /*html*/`
            <div class="changelog-msg-row">
              <span class="changelog-msg-label changelog-msg-label--dev">Dev notes:</span>
              <p class="changelog-msg">${devStr}</p>
            </div>` : ''}`;
        return /*html*/`
          <div class="changelog-entry">
            <div class="changelog-header-row">
              <span class="changelog-version">${version}</span>
              ${dateStr ? `<span class="changelog-date">${dateStr}</span>` : ''}
            </div>
            ${msgBody}
          </div>`;
      }).join('');
    } catch {
      container.innerHTML = '<p class="changelog-empty">No changelog available.</p>';
    }
  }
}

if (!customElements.get('as-changelog')) {
  customElements.define('as-changelog', AsChangelog);
} else {
  console.warn('as-changelog already defined');
}
